

import { useState } from "react";
import toasted from "../config/toasted";
import { collection, query, where, getDocs, Timestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import { categoryAll } from "../Datas/categoryPosts";


export const blogListUrl = 'https://opensheet.elk.sh/1jkN8NHNvYJCLjPOfMb8y1vaDFrxPn_qgHeX5x0k-jMI/blogs';
export const itzpromptUrl = 'https://opensheet.elk.sh/1jkN8NHNvYJCLjPOfMb8y1vaDFrxPn_qgHeX5x0k-jMI/prompts';


// Support message for prompt copying & sharing
export const supportMessage = `Maintain exact facial features, hairstyle, and skin tone as the reference image. Keep face 100% identical to masked reference.`;



// Component to display an image with a fade-in effect upon loading
export function FadeImage({ src, alt }) {
     const [loaded, setLoaded] = useState(false);

     return (
          <img
               src={src}
               alt={alt}
               onLoad={() => setLoaded(true)}
               className={loaded ? "loaded" : ""}
               loading="lazy"
          />
     );
}

// 
export const getOptimizedImage = ({
     publicId,
     width = 1200,
     watermark = true,
}) => {
     const CLOUD = "dvlqi71yj";
     const base = `https://res.cloudinary.com/${CLOUD}/image/upload`;

     const baseTransform = `w_${width},c_limit,f_auto,q_auto`;

     const watermarkLayer = watermark
          ? `l_My%20Brand:apple-touch-icon_npxiql,w_70,o_50/fl_layer_apply,g_south_east,x_20,y_20`
          : "";

     return watermark
          ? `${base}/${baseTransform}/${watermarkLayer}/${publicId}`
          : `${base}/${baseTransform}/${publicId}`;
};





// Function to copy prompt text to clipboard
// Uses the Clipboard 
export const handleCopy = (promptText) => {
     navigator.clipboard.writeText(promptText)
          .then(() => {
               toasted.success("Prompt copied to clipboard!");
          })
          .catch((err) => {
               toasted.error("Failed to copy: ", err);
          });
};


// Function to format Firestore Timestamp or string date to "DD MMM YYYY"
export const formatCreatedAt = (createdAt) => {
     if (!createdAt) return "—";

     // 🔹 Firestore Timestamp
     if (typeof createdAt === "object" && createdAt.seconds) {
          const date = new Date(createdAt.seconds * 1000);
          return date.toLocaleDateString("en-IN", {
               day: "2-digit",
               month: "short",
               year: "numeric",
          });
     }

     // 🔹 String date
     if (typeof createdAt === "string") {
          try {
               // "13/12/2025, 10:30:45"
               if (createdAt.includes(",")) {
                    const [datePart] = createdAt.split(",");
                    const [day, month, year] = datePart.split("/").map(Number);
                    return new Date(year, month - 1, day).toLocaleDateString("en-IN", {
                         day: "2-digit",
                         month: "short",
                         year: "numeric",
                    });
               }

               // "13/12/2025"
               const [day, month, year] = createdAt.split("/").map(Number);
               return new Date(year, month - 1, day).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
               });
          } catch {
               return "—";
          }
     }

     return "—";
};


// Function to calculate total likes from an array of prompt objects
// Each object should have a 'likes' property
export const calculateTotalLikes = (prompts) => {
     return prompts.reduce((sum, item) => {
          return sum + Number(item.likes || 0);
     }, 0);
};


// Function to calculate total pending and approved prompts
export const calculateTotalPrompts = (prompts) => {
     const totalPending = prompts.filter(item => item.status === "pending").length;
     const totalSuccess = prompts.filter(item => item.status === "success").length;
     return { totalPending, totalSuccess };
};

// Function to share text using Web Share API
export const shareTextOf = async (txt) => {
     if (!navigator.share) {
          toasted.error("Sharing not supported here.");
          return;
     }

     try {
          await navigator.share({ text: txt });
          toasted.success("Shared successfully");
     } catch (err) {
          // ❌ User pressed Cancel → ignore silently
          if (err.name !== "AbortError") {
               console.error(err);
               toasted.error("Something went wrong while sharing.");
          }
     }
};


// Function to calculate subscription expiry date based on start timestamp and plan title
// Plan title should contain duration like "7 Day Plan" or "1 Month Plan"
// Returns a Date object or null if input is invalid
export const getExpiryDate = (timestamp, planTitle) => {
     if (!timestamp || !planTitle) return null;

     const startDate = new Date(timestamp);
     const expiryDate = new Date(startDate);

     const match = planTitle.match(/\d+/);
     const value = match ? parseInt(match[0]) : 0;

     if (!value) return null;

     if (planTitle.toLowerCase().includes("day")) {
          expiryDate.setDate(expiryDate.getDate() + value);
     }

     if (planTitle.toLowerCase().includes("month")) {
          expiryDate.setMonth(expiryDate.getMonth() + value);
     }

     return expiryDate;
};


// Function to format follower count into a more readable string (e.g., 1500 → "1.5K")
// Handles numbers less than 1000 as normal, and formats larger numbers with "K" suffix
export function formatFollowers(number) {
     if (number < 1000) return number.toString(); // 999 or less

     // Thousand (1K - 99.9K)
     if (number < 100000) {
          const formatted = (number / 1000).toFixed(1);
          return formatted.endsWith(".0")
               ? `${formatted.slice(0, -2)}K`
               : `${formatted}K`;
     }

     // Lakh (1L - 99.9L)
     if (number < 10000000) {
          const formatted = (number / 100000).toFixed(1);
          return formatted.endsWith(".0")
               ? `${formatted.slice(0, -2)}L`
               : `${formatted}L`;
     }

     // Crore (1Cr+)
     const formatted = (number / 10000000).toFixed(1);
     return formatted.endsWith(".0")
          ? `${formatted.slice(0, -2)}Cr`
          : `${formatted}Cr`;
}




/**
 * Get number of prompts submitted by user in last 24 hours.
 * Works with Firestore Timestamp or string ISO saved in `approvedAt`.
 * @param {string} userId - Firebase UID
 * @returns {Promise<number>} - Count of prompts in last 24h
 */
export const getLast24HourPromptCount = async (userId) => {
    if (!userId) return 0;

    const last24Date = new Date(Date.now() - 24 * 60 * 60 * 1000);

    try {
        // Try Firestore Timestamp query first (fast + indexed)
        const now = Timestamp.now();
        const last24Timestamp = new Timestamp(now.seconds - 24 * 60 * 60, now.nanoseconds);

        const q = query(
            collection(db, "prompts"),
            where("userId", "==", userId),
            where("approvedAt", ">=", last24Timestamp)
        );

        const snapshot = await getDocs(q);
        return snapshot.size;
    } catch (err) {
        console.warn("Timestamp query failed, fallback to JS filtering:", err);

        // Fallback: fetch all user prompts and filter in JS
        const q = query(collection(db, "prompts"), where("userId", "==", userId));
        const snapshot = await getDocs(q);

        const count = snapshot.docs.filter(doc => {
            const approvedAt = doc.data().approvedAt;
            if (!approvedAt) return false;

            let approvedDate;
            if (approvedAt.toDate) {
                // Firestore Timestamp
                approvedDate = approvedAt.toDate();
            } else {
                // ISO string
                approvedDate = new Date(approvedAt);
            }

            return approvedDate >= last24Date;
        }).length;

        return count;
    }
};



export const totalPrompts = Object.values(categoryAll?.categoryPromptList || {}).reduce((acc, item) => acc + (item?.prompts?.length || 0), 0);


// download image functions 

export const handleDownload = async (url) => {
     try {
          const response = await fetch(url);
          const blob = await response.blob();

          const blobUrl = window.URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = 'heyprompt-image.jpg';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
     } catch (err) {
          console.error('Download failed', err);
     }
};


// Function to generate a unique filename for downloaded images based on current timestamp

export const formatDate = (date) => {
     if (!date) return 'N/A';

     return new Date(date).toLocaleString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
     });
};